import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, Calendar, User, Info, Tag, ExternalLink, Heart, Share2, Eye } from 'lucide-react';
import { demoNfts } from '@/services/demoData';
import { useAccount } from 'wagmi';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

export default function NFTDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('details');
  const [liked, setLiked] = useState(false);
  const { isConnected } = useAccount();
  
  // Use demo data
  const nft = demoNfts[0]; // Using first NFT as example
  
  return (
    <div className="container-custom py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left Column - NFT Image */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="sticky top-24">
            <motion.div 
              className="rounded-2xl overflow-hidden mb-6 bg-dark-800 border border-dark-700 shadow-3d hover:shadow-3d-hover transition-all duration-300"
              whileHover={{ scale: 1.02 }}
            >
              <img 
                src={nft.image} 
                alt={nft.name}
                className="w-full aspect-square object-contain"
              />
            </motion.div>
            
            <div className="flex space-x-4 mb-6">
              <Button 
                variant="outline" 
                className="flex-1 shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
                onClick={() => setLiked(!liked)}
              >
                <Heart className={`h-5 w-5 mr-2 ${liked ? 'fill-red-500 text-red-500' : ''}`} />
                {liked ? 'Liked' : 'Like'}
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
              >
                <Share2 className="h-5 w-5 mr-2" />
                Share
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
              >
                <Eye className="h-5 w-5 mr-2" />
                View on IPFS
              </Button>
            </div>
            
            <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Tag className="h-5 w-5 text-primary-500 mr-2" />
                    <span className="text-sm">Current Price</span>
                  </div>
                  <span className="text-xl font-bold text-shadow">{nft.price} DUSD</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-primary-500 mr-2" />
                    <span className="text-sm">Minted</span>
                  </div>
                  <span className="text-sm">
                    {new Date(nft.mintedAt).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-primary-500 mr-2" />
                    <span className="text-sm">Creator</span>
                  </div>
                  <span className="text-sm font-medium">{nft.creatorName}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-primary-500 mr-2" />
                    <span className="text-sm">Owner</span>
                  </div>
                  <span className="text-sm font-medium">
                    {nft.owner.substring(0, 6)}...{nft.owner.substring(nft.owner.length - 4)}
                  </span>
                </div>
                
                <div className="pt-4 border-t">
                  <Button 
                    className="w-full shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
                    onClick={() => {
                      if (isConnected) {
                        // Handle purchase logic here
                        toast.success("Purchase initiated! This is a demo transaction.");
                      } else {
                        toast.error("Please connect your wallet to make a purchase");
                      }
                    }}
                  >
                    {nft.forSale ? 'Buy Now' : 'Make Offer'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
        
        {/* Right Column - NFT Details */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h1 className="text-3xl font-bold mb-2 text-shadow">{nft.name}</h1>
          
          <div className="flex items-center mb-6">
            <div className="w-8 h-8 rounded-full bg-dark-700 mr-2 shadow-3d"></div>
            <span className="text-sm font-medium">Created by <span className="text-primary-500">{nft.creatorName}</span></span>
          </div>
          
          <p className="text-gray-400 mb-8">{nft.description}</p>
          
          <Tabs defaultValue="details" className="mb-8">
            <TabsList className="shadow-3d">
              <TabsTrigger 
                value="details" 
                onClick={() => setActiveTab('details')}
                className="hover:shadow-3d-hover transition-all duration-300"
              >
                Details
              </TabsTrigger>
              <TabsTrigger 
                value="attributes" 
                onClick={() => setActiveTab('attributes')}
                className="hover:shadow-3d-hover transition-all duration-300"
              >
                Attributes
              </TabsTrigger>
              <TabsTrigger 
                value="history" 
                onClick={() => setActiveTab('history')}
                className="hover:shadow-3d-hover transition-all duration-300"
              >
                History
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="mt-6">
              <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium mb-2 text-shadow">About this NFT</h3>
                      <p className="text-gray-400">
                        {nft.description || "This is a unique digital collectible created on the FTTF.World platform. Each NFT is one-of-a-kind and represents ownership of the digital item."}
                      </p>
                    </div>
                    
                    <div className="pt-4 border-t">
                      <h3 className="text-lg font-medium mb-2 text-shadow">Contract Details</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Contract Address</span>
                          <span className="text-sm font-mono">{nft.contractAddress}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Token ID</span>
                          <span>{nft.tokenId}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Token Standard</span>
                          <span>{nft.tokenType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Chain</span>
                          <span>Sonic Blaze Testnet</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Creator Royalties</span>
                          <span>{nft.royaltyPercentage}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="attributes" className="mt-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {nft.attributes.map((attr, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
                      <CardContent className="p-4 text-center">
                        <p className="text-xs text-gray-400 uppercase">{attr.trait_type}</p>
                        <p className="text-lg font-medium text-shadow">{attr.value}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="history" className="mt-6">
              <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
                <CardContent className="p-4">
                  <div className="space-y-4">
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-start pb-4 border-b"
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden mr-4 flex-shrink-0 shadow-3d">
                        <div className="w-full h-full bg-primary-600/20"></div>
                      </div>
                      <div>
                        <p className="text-sm">
                          <span className="font-medium">Minted</span>
                          {' by '}
                          <span className="font-medium">{nft.creatorName}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(nft.mintedAt).toLocaleString()}
                        </p>
                      </div>
                      <div className="ml-auto text-right">
                        <p className="text-sm font-medium">-</p>
                      </div>
                    </motion.div>
                    
                    {nft.forSale && (
                      <motion.div 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        className="flex items-start pb-4 border-b"
                      >
                        <div className="w-10 h-10 rounded-full overflow-hidden mr-4 flex-shrink-0 shadow-3d">
                          <div className="w-full h-full bg-primary-600/20"></div>
                        </div>
                        <div>
                          <p className="text-sm">
                            <span className="font-medium">Listed</span>
                            {' by '}
                            <span className="font-medium">{nft.creatorName}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(Date.now() - 86400000).toLocaleString()}
                          </p>
                        </div>
                        <div className="ml-auto text-right">
                          <p className="text-sm font-medium">{nft.price} DUSD</p>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          
          <h3 className="text-xl font-bold mb-4 text-shadow">More from this collection</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {demoNfts.slice(0, 3).map((relatedNft, index) => (
              <motion.div
                key={relatedNft.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Card className="overflow-hidden shadow-3d hover:shadow-3d-hover transition-all duration-300">
                  <div className="aspect-square bg-dark-700">
                    <img 
                      src={relatedNft.image} 
                      alt={relatedNft.name}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-medium mb-1 text-shadow">{relatedNft.name}</h3>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">#{relatedNft.tokenId}</span>
                      <span className="text-primary-500 font-medium text-shadow">{relatedNft.price} DUSD</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
